import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { StudentAdmissionFormData, FeeStructure, SchoolClass } from '../../types';
import { feeStructureApi, schoolDataApi } from '../../services/api';
import { sortClasses, getClassDisplayOrder } from '../../utils/classUtils';
import { toast } from 'react-toastify';
import {
  Upload,
  X,
  MapPin,
  GraduationCap,
  FileText,
  Phone,
  Users,
  CreditCard,
  Home,
  Bus,
  User,
  ChevronLeft,
  ChevronRight,
  Check,
  Calendar,
  IdCard,
  Briefcase
} from 'lucide-react';

interface StudentAdmissionFormProps {
  onSubmit: (data: StudentAdmissionFormData) => void;
  loading?: boolean;
  initialData?: Partial<StudentAdmissionFormData>;
  onCancel?: () => void;
  registrationId?: string; // Added for auto-save functionality
  registrationData?: {
    name: string;
    dateOfBirth: string;
    gender: string;
    parentName: string;
    parentContact: string;
    parentEmail: string;
    preferredClass: string;
  };
}


const STEPS = [
  { id: 1, title: 'Student Details', icon: User },
  { id: 2, title: 'Parents Details', icon: Users },
  { id: 3, title: 'Documents Upload', icon: FileText },
  { id: 4, title: 'Bank Details', icon: Briefcase },
  { id: 5, title: 'Fee & Payments', icon: CreditCard }
];

const StudentAdmissionForm: React.FC<StudentAdmissionFormProps> = ({
  onSubmit,
  loading = false,
  initialData,
  onCancel,
  registrationId,
  registrationData
}) => {
  console.log('StudentAdmissionForm loaded with registrationId:', registrationId);
  const [currentStep, setCurrentStep] = useState(1);
  const [sameAddress, setSameAddress] = useState(false);
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);
  const [selectedFeeStructure, setSelectedFeeStructure] = useState<FeeStructure | null>(null);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [documents, setDocuments] = useState<{ [key: string]: File | null }>({
    photo: null,
    aadharCard: null,
    birthCertificate: null,
    transferCertificate: null,
    marksheet: null,
    medicalCertificate: null,
    casteCertificate: null,
    incomeCertificate: null
  });
  const [documentPreviews, setDocumentPreviews] = useState<{ [key: string]: string }>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    getValues,
    reset,
    setError,
    clearErrors
  } = useForm<StudentAdmissionFormData>({
    defaultValues: {
      ...initialData,
      class: registrationData?.preferredClass || initialData?.class,
      fatherName: registrationData?.parentName || initialData?.fatherName,
      parentContact: registrationData?.parentContact || initialData?.parentContact,
      parentEmail: registrationData?.parentEmail || initialData?.parentEmail,
      dateOfBirth: registrationData?.dateOfBirth || initialData?.dateOfBirth,
      gender: registrationData?.gender as any || initialData?.gender,
      sameAddress: false,
      needsHostel: false,
      needsTransport: false,
      isRteQuota: false,
      motherName: ''
    }
  });

  const watchedClass = watch('class');
  const permanentAddress = watch('permanentAddress');
  const motherNameValue = watch('motherName');
  const watchedIsRteQuota = watch('isRteQuota');

  // Load fee structures and classes when component mounts
  useEffect(() => {
    loadFeeStructures();
    loadClasses();
    loadDraftData();

    // Manually register motherName field without global validation
    register('motherName');
  }, [register]);

  // Load any existing draft data
  const loadDraftData = async () => {
    if (!registrationId) return;

    try {
      const draftData = await schoolDataApi.students.getDraftAdmission(registrationId);
      if (draftData) {
        console.log('Loading draft data:', draftData);
        const data = draftData.formData; // Updated to use formData instead of draftData

        // Debug: Log specific fields to check for cross-contamination
        console.log('Draft data - caste:', data.caste);
        console.log('Draft data - motherName:', data.motherName);
        console.log('Draft data - religion:', data.religion);
        console.log('Draft data - emergencyContact:', data.emergencyContact);

        // Reset the form with the current form values merged with draft data
        const currentFormValues = getValues();
        console.log('Current form values before merge:', currentFormValues);

        const mergedFormData = {
          ...currentFormValues,
          ...data // Draft data takes priority
        };

        console.log('Merged form data - caste:', mergedFormData.caste);
        console.log('Merged form data - motherName:', mergedFormData.motherName);
        console.log('Resetting form with merged data:', mergedFormData);

        // CRITICAL FIX: Detect and clear field contamination
        if (data.motherName === data.caste || mergedFormData.motherName === mergedFormData.caste) {
          console.log('DETECTED CONTAMINATION: motherName contains caste value, clearing it');
          mergedFormData.motherName = '';
        }
        if (data.fatherName === data.religion || mergedFormData.fatherName === mergedFormData.religion) {
          console.log('DETECTED CONTAMINATION: fatherName contains religion value, clearing it');
          mergedFormData.fatherName = '';
        }

        // Only set motherName and fatherName if they're valid (not contaminated)
        if (data.motherName && data.motherName !== data.caste && data.motherName !== data.religion && data.motherName.trim() !== '') {
          mergedFormData.motherName = data.motherName;
        } else {
          mergedFormData.motherName = '';
        }

        if (data.fatherName && data.fatherName !== data.religion && data.fatherName !== data.caste && data.fatherName.trim() !== '') {
          mergedFormData.fatherName = data.fatherName;
        } else {
          mergedFormData.fatherName = '';
        }

        console.log('After explicit field fix - motherName:', mergedFormData.motherName);
        reset(mergedFormData);

        // Double-check: Force clear contaminated fields and set correct values
        setTimeout(() => {
          const currentMotherName = getValues('motherName');
          const currentFatherName = getValues('fatherName');
          const currentCaste = getValues('caste');
          const currentReligion = getValues('religion');

          // Clear motherName if it contains caste value
          if (currentMotherName === currentCaste && currentCaste) {
            console.log('FORCE CLEARING CONTAMINATED motherName:', currentMotherName);
            setValue('motherName', '');
          } else if (data.motherName && data.motherName !== data.caste && data.motherName !== data.religion && data.motherName.trim() !== '') {
            setValue('motherName', data.motherName);
            console.log('Force set motherName to:', data.motherName);
          }

          // Clear fatherName if it contains religion value
          if (currentFatherName === currentReligion && currentReligion) {
            console.log('FORCE CLEARING CONTAMINATED fatherName:', currentFatherName);
            setValue('fatherName', '');
          } else if (data.fatherName && data.fatherName !== data.religion && data.fatherName !== data.caste && data.fatherName.trim() !== '') {
            setValue('fatherName', data.fatherName);
            console.log('Force set fatherName to:', data.fatherName);
          }
        }, 100);

        setCurrentStep(draftData.currentStep);
        setLastSaved(new Date()); // Set initial save time for restored data
        toast.info(`Previous form data restored from Step ${draftData.currentStep}`);
      }
    } catch (error) {
      console.error('Error loading draft data:', error);
    }
  };

  // Update fee structure when class changes
  useEffect(() => {
    if (watchedClass && feeStructures.length > 0) {
      console.log('Fee structures available:', feeStructures.map(fs => ({
        id: fs.id,
        className: fs.className,
        type: fs.type,
        isActive: fs.isActive
      })));
      console.log('Selected class:', watchedClass);

      // Look for admission fee structure for the selected class
      // Handle different class name formats (e.g., "1st" vs "Class 1")
      const normalizeClassName = (className: string) => {
        return className.toLowerCase()
          .replace(/class\s*/i, '') // Remove "class" prefix
          .replace(/st|nd|rd|th/g, '') // Remove ordinal suffixes
          .trim();
      };

      const normalizedWatchedClass = normalizeClassName(watchedClass);
      console.log('Normalized selected class:', normalizedWatchedClass);

      const matchingFeeStructure = feeStructures.find(fs => {
        const normalizedFeeClassName = normalizeClassName(fs.className);
        console.log(`Comparing: "${normalizedWatchedClass}" === "${normalizedFeeClassName}"`);
        return normalizedFeeClassName === normalizedWatchedClass &&
               fs.type === 'admission' &&
               fs.isActive;
      });

      console.log('Matching fee structure:', matchingFeeStructure);

      setSelectedFeeStructure(matchingFeeStructure || null);
      if (matchingFeeStructure) {
        setValue('selectedFeeStructure', matchingFeeStructure.id);
        setValue('feeAmount', matchingFeeStructure.totalAmount);
      } else {
        // Clear values if no matching fee structure found
        setValue('selectedFeeStructure', '');
        setValue('feeAmount', 0);
      }
    }
  }, [watchedClass, feeStructures, setValue]);

  // Handle address copying
  useEffect(() => {
    if (sameAddress && permanentAddress) {
      setValue('currentAddress', permanentAddress);
    }
  }, [permanentAddress, sameAddress, setValue]);


  const loadFeeStructures = async () => {
    try {
      const structures = await feeStructureApi.getFeeStructures();
      console.log('Loaded fee structures:', structures);
      setFeeStructures(structures);
    } catch (error) {
      console.error('Error loading fee structures:', error);
    }
  };

  const loadClasses = async () => {
    try {
      setLoadingClasses(true);
      const data = await schoolDataApi.classes.getAll();
      const activeClasses = data.filter(cls => cls.isActive);
      const sortedClasses = sortClasses(activeClasses);
      setClasses(sortedClasses);
    } catch (error) {
      console.error('Error loading classes:', error);
      // Fallback to hardcoded classes if API fails
      const fallbackClasses = [
        'Nursery', 'LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
        'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'
      ].map((name, index) => ({
        id: (index + 1).toString(),
        name,
        sections: ['A', 'B', 'C'],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }));
      const sortedFallbackClasses = sortClasses(fallbackClasses);
      setClasses(sortedFallbackClasses);
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleSameAddressChange = (checked: boolean) => {
    setSameAddress(checked);
    setValue('sameAddress', checked);
    if (checked && permanentAddress) {
      setValue('currentAddress', permanentAddress);
    } else if (!checked) {
      setValue('currentAddress', '');
    }
  };

  const handleDocumentUpload = (documentType: string, file: File) => {
    setDocuments(prev => ({ ...prev, [documentType]: file }));

    const reader = new FileReader();
    reader.onload = (e) => {
      setDocumentPreviews(prev => ({ ...prev, [documentType]: e.target?.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const removeDocument = (documentType: string) => {
    setDocuments(prev => ({ ...prev, [documentType]: null }));
    setDocumentPreviews(prev => ({ ...prev, [documentType]: '' }));
  };

  const nextStep = async () => {
    // Define which fields to validate for each step
    let fieldsToValidate: (keyof StudentAdmissionFormData)[] = [];

    switch (currentStep) {
      case 1: // Student Details
        fieldsToValidate = ['permanentAddress', 'currentAddress', 'aadharNumber', 'dateOfBirth', 'gender', 'class'];
        break;
      case 2: // Parents Details
        fieldsToValidate = ['parentContact', 'parentEmail', 'motherName', 'fatherName'];
        break;
      case 3: // Documents Upload
        fieldsToValidate = []; // Documents are optional, no required validation
        break;
      case 4: // Bank Details
        fieldsToValidate = ['bankAccountNumber', 'bankName', 'ifscCode', 'accountHolderName'];
        break;
      case 5: // Fee & Payments
        fieldsToValidate = ['selectedFeeStructure'];
        break;
      default:
        fieldsToValidate = [];
    }

    // Only validate fields relevant to current step
    const isValid = fieldsToValidate.length > 0 ? await trigger(fieldsToValidate) : true;

    if (isValid && currentStep < STEPS.length) {
      try {
        await saveDraftData(currentStep + 1);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        console.error('Failed to save draft data:', error);
        toast.error('Failed to save form data. Please try again.');
      }
    } else if (!isValid) {
      // Show specific validation errors as toast
      showValidationErrors();
    }
  };

  // Save current form data as draft
  const saveDraftData = async (stepToSave: number) => {
    if (!registrationId) {
      console.error('No registrationId provided - cannot save draft data');
      toast.error('Cannot save form data - missing registration ID');
      return;
    }

    console.log('Starting to save draft data for registrationId:', registrationId, 'step:', stepToSave);

    try {
      setSaving(true);
      const currentFormData = getValues();

      // Create draft data object (exclude files for now as they need special handling)
      const rawDraftData: Partial<StudentAdmissionFormData> = {
        permanentAddress: currentFormData.permanentAddress,
        currentAddress: currentFormData.currentAddress,
        sameAddress: currentFormData.sameAddress,
        aadharNumber: currentFormData.aadharNumber,
        dateOfBirth: currentFormData.dateOfBirth,
        gender: currentFormData.gender,
        previousSchool: currentFormData.previousSchool,
        medicalConditions: currentFormData.medicalConditions,
        caste: currentFormData.caste,
        category: currentFormData.category,
        religion: currentFormData.religion,
        penNumber: currentFormData.penNumber,
        apaarId: currentFormData.apaarId,
        isRteQuota: currentFormData.isRteQuota,
        annualIncome: currentFormData.annualIncome,
        parentContact: currentFormData.parentContact,
        parentEmail: currentFormData.parentEmail,
        fatherOccupation: currentFormData.fatherOccupation,
        motherOccupation: currentFormData.motherOccupation,
        emergencyContact: currentFormData.emergencyContact,
        motherName: currentFormData.motherName,
        fatherName: currentFormData.fatherName,
        class: currentFormData.class,
        selectedFeeStructure: currentFormData.selectedFeeStructure,
        feeAmount: currentFormData.feeAmount,
        bankAccountNumber: currentFormData.bankAccountNumber,
        bankName: currentFormData.bankName,
        ifscCode: currentFormData.ifscCode,
        accountHolderName: currentFormData.accountHolderName,
        needsHostel: currentFormData.needsHostel,
        needsTransport: currentFormData.needsTransport,
      };

      // Filter out undefined values (Firebase doesn't allow undefined)
      const draftData: Partial<StudentAdmissionFormData> = Object.fromEntries(
        Object.entries(rawDraftData).filter(([_, value]) => value !== undefined)
      ) as Partial<StudentAdmissionFormData>;

      // Debug: Log specific fields being saved to check for cross-contamination
      console.log('Raw form data keys with undefined:', Object.keys(rawDraftData).filter(key => rawDraftData[key as keyof typeof rawDraftData] === undefined));
      console.log('Filtered draft data keys:', Object.keys(draftData));
      console.log('Saving draft data - caste:', draftData.caste);
      console.log('Saving draft data - motherName:', draftData.motherName);
      console.log('Saving draft data - religion:', draftData.religion);
      console.log('Saving draft data - emergencyContact:', draftData.emergencyContact);
      console.log('Saving draft data:', { registrationId, stepToSave, draftData });

      await schoolDataApi.students.saveDraftAdmission(registrationId, draftData, stepToSave);
      console.log('Draft saved successfully for step:', stepToSave);
      setLastSaved(new Date());
      toast.success('Progress saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save form data');
    } finally {
      setSaving(false);
    }
  };

  // Removed auto-save functionality - now only saves on Next button click

  const prevStep = async () => {
    if (currentStep > 1) {
      try {
        // Save current form data before going back
        await saveDraftData(currentStep - 1);
        setCurrentStep(currentStep - 1);
      } catch (error) {
        console.error('Failed to save draft data:', error);
        toast.error('Failed to save form data. Please try again.');
      }
    }
  };

  // Helper function to validate individual field and show toast
  const validateFieldAndShowToast = async (fieldName: string) => {
    const isValid = await trigger(fieldName as keyof StudentAdmissionFormData);
    if (!isValid && errors[fieldName as keyof typeof errors]) {
      const error = errors[fieldName as keyof typeof errors];
      const fieldLabels: { [key: string]: string } = {
        parentContact: 'Contact Number',
        parentEmail: 'Parent Email',
        permanentAddress: 'Permanent Address',
        currentAddress: 'Current Address',
        aadharNumber: 'Aadhar Number',
        dateOfBirth: 'Date of Birth',
        class: 'Class',
        fatherName: 'Father Name',
        motherName: 'Mother Name',
        fatherOccupation: 'Father Occupation',
        motherOccupation: 'Mother Occupation',
        emergencyContact: 'Emergency Contact',
        bankAccountNumber: 'Bank Account Number',
        bankName: 'Bank Name',
        ifscCode: 'IFSC Code',
        accountHolderName: 'Account Holder Name'
      };

      const fieldLabel = fieldLabels[fieldName] || fieldName;
      toast.error(`${fieldLabel}: ${error?.message || 'This field is required'}`);
    }
  };

  // Helper function to show validation errors as toast
  const showValidationErrors = () => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const firstErrorField = errorFields[0] as keyof typeof errors;
      const firstError = errors[firstErrorField];

      // Show specific error message with field name
      const fieldLabels: { [key: string]: string } = {
        parentContact: 'Contact Number',
        parentEmail: 'Parent Email',
        permanentAddress: 'Permanent Address',
        currentAddress: 'Current Address',
        aadharNumber: 'Aadhar Number',
        class: 'Class',
        fatherName: 'Father Name',
        motherName: 'Mother Name',
        fatherOccupation: 'Father Occupation',
        motherOccupation: 'Mother Occupation',
        emergencyContact: 'Emergency Contact',
        bankAccountNumber: 'Bank Account Number',
        bankName: 'Bank Name',
        ifscCode: 'IFSC Code',
        accountHolderName: 'Account Holder Name'
      };

      const fieldLabel = fieldLabels[firstErrorField] || firstErrorField;
      toast.error(`${fieldLabel}: ${firstError?.message || 'This field is required'}`);
    } else {
      toast.error('Please fill in all required fields correctly');
    }
  };

  const onFormSubmit: SubmitHandler<StudentAdmissionFormData> = async (data) => {
    // Validate all fields before submission
    const isValid = await trigger();
    if (!isValid) {
      showValidationErrors();
      return;
    }

    const formData = {
      ...data,
      ...documents
    };

    // Update admission status to submitted instead of deleting
    if (registrationId) {
      try {
        await schoolDataApi.students.saveDraftAdmission(registrationId, data, 5, undefined, 'submitted');
        console.log('Admission status updated to submitted');
      } catch (error) {
        console.error('Error updating admission status:', error);
      }
    }

    onSubmit(formData);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isCompleted = currentStep > step.id;
        const isCurrent = currentStep === step.id;

        return (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
              isCompleted
                ? 'bg-[#4ade80] border-[#4ade80] text-white'
                : isCurrent
                ? 'bg-[#e2916d] border-[#e2916d] text-white'
                : 'bg-[#211c15] border-[#3a3122] text-[#8a7f6e]'
            }`}>
              {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
            </div>
            <div className="ml-3 hidden md:block">
              <p className={`text-sm font-medium ${isCurrent ? 'text-[#e2916d]' : 'text-[#b9ad9b]'}`}>
                {step.title}
              </p>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-8 md:w-16 h-0.5 ml-3 md:ml-6 ${
                isCompleted ? 'bg-[#4ade80]' : 'bg-[#3a3122]'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderStudentDetails = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
        <User className="w-5 h-5 mr-2 text-[#e2916d]" />
        Student Details
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Student Name from Registration */}
        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Student Name *
          </label>
          <input
            type="text"
            value={registrationData?.name || ''}
            readOnly
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#28221a] text-[#f3ede2] cursor-not-allowed"
            placeholder="Student name from registration"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Aadhar Number *
          </label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={12}
            {...register('aadharNumber', {
              required: 'Aadhar number is required',
              pattern: {
                value: /^\d{12}$/,
                message: 'Aadhar number must be exactly 12 digits'
              },
              validate: {
                validAadhar: (value) => {
                  if (!value) return 'Aadhar number is required';
                  if (value.length !== 12) return 'Aadhar number must be exactly 12 digits';
                  if (!/^\d{12}$/.test(value)) return 'Aadhar number should contain only digits';
                  // Additional Aadhar validation logic can be added here
                  return true;
                }
              }
            })}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 12);
              setValue('aadharNumber', digitsOnly, { shouldValidate: true });
            }}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter 12-digit Aadhar number"
            onBlur={() => validateFieldAndShowToast('aadharNumber')}
          />
          {errors.aadharNumber && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.aadharNumber.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Date of Birth *
          </label>
          <input
            type="date"
            {...register('dateOfBirth', { required: 'Date of birth is required' })}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            onBlur={() => validateFieldAndShowToast('dateOfBirth')}
          />
          {errors.dateOfBirth && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.dateOfBirth.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Gender *
          </label>
          <select
            {...register('gender', { required: 'Gender is required' })}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            disabled={!!registrationData?.gender}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
          {errors.gender && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.gender.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Caste
          </label>
          <input
            type="text"
            {...register('caste')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter caste"
            id="field-caste-personal-details"
            data-field="caste"
            autoComplete="off"
            onChange={(e) => {
              console.log('Caste field changed to:', e.target.value);
              // Ensure this only updates the caste field
              setValue('caste', e.target.value);
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Category *
          </label>
          <select
            {...register('category', { required: 'Category is required' })}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
          >
            <option value="">Select Category</option>
            <option value="General">General</option>
            <option value="OBC">OBC</option>
            <option value="SC">SC</option>
            <option value="ST">ST</option>
            <option value="EWS">EWS</option>
            <option value="Other">Other</option>
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.category.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Religion
          </label>
          <select
            {...register('religion')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
          >
            <option value="">Select Religion</option>
            <option value="Hindu">Hindu</option>
            <option value="Muslim">Muslim</option>
            <option value="Christian">Christian</option>
            <option value="Sikh">Sikh</option>
            <option value="Buddhist">Buddhist</option>
            <option value="Jain">Jain</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>

        {/* Show PEN and APAAR ID for classes above 1 */}
        {watchedClass && getClassDisplayOrder(watchedClass) > 4 && (
          <>
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                PEN Number
              </label>
              <input
                type="text"
                {...register('penNumber')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter PEN number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                APAAR ID
              </label>
              <input
                type="text"
                {...register('apaarId')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter APAAR ID"
              />
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Previous School
          </label>
          <input
            type="text"
            {...register('previousSchool')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter previous school name"
          />
        </div>
      </div>

      <div className="border border-[#3a3122] rounded-lg p-4 space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isRteQuota"
            {...register('isRteQuota')}
            className="w-4 h-4 text-[#e2916d] border-[#4a4030] rounded focus:ring-[#e2916d] focus:ring-2"
          />
          <label htmlFor="isRteQuota" className="ml-2 text-sm font-medium text-[#b9ad9b]">
            Applying under RTE 25% reservation quota (EWS / disadvantaged group)
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
              Annual Family Income (₹)
            </label>
            <input
              type="number"
              min="0"
              {...register('annualIncome', {
                min: { value: 0, message: 'Annual income cannot be negative' }
              })}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              placeholder="Enter annual family income"
            />
            {errors.annualIncome && (
              <p className="mt-1 text-sm text-[#f38b83]">{errors.annualIncome.message}</p>
            )}
            {watchedIsRteQuota && (
              <p className="mt-1 text-sm text-[#8a7f6e]">
                EWS eligibility typically requires annual family income below ₹1,00,000 (varies by state).
              </p>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
          Permanent Address *
        </label>
        <textarea
          {...register('permanentAddress', { required: 'Permanent address is required' })}
          rows={3}
          className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
          placeholder="Enter complete permanent address"
          onBlur={() => validateFieldAndShowToast('permanentAddress')}
        />
        {errors.permanentAddress && (
          <p className="mt-1 text-sm text-[#f38b83]">{errors.permanentAddress.message}</p>
        )}
      </div>

      <div>
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="sameAddress"
            checked={sameAddress}
            onChange={(e) => handleSameAddressChange(e.target.checked)}
            className="w-4 h-4 text-[#e2916d] border-[#4a4030] rounded focus:ring-[#e2916d] focus:ring-2"
          />
          <label htmlFor="sameAddress" className="ml-2 text-sm font-medium text-[#b9ad9b]">
            Current address is same as permanent address
          </label>
        </div>
        <textarea
          {...register('currentAddress', { required: 'Current address is required' })}
          rows={3}
          className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
          placeholder={sameAddress ? "Same as permanent address" : "Enter current residential address"}
          disabled={sameAddress}
        />
        {errors.currentAddress && (
          <p className="mt-1 text-sm text-[#f38b83]">{errors.currentAddress.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
          Medical Conditions
        </label>
        <textarea
          {...register('medicalConditions')}
          rows={2}
          className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
          placeholder="Any medical conditions or allergies"
        />
      </div>

      <div>
        <h4 className="text-md font-medium text-[#f3ede2] mb-4">Class Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
              Class *
            </label>
            <select
              {...register('class', { required: 'Class is required' })}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              disabled={loadingClasses}
              onBlur={() => validateFieldAndShowToast('class')}
            >
              <option value="">{loadingClasses ? 'Loading classes...' : 'Select Class'}</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.name}>{cls.name}</option>
              ))}
            </select>
            {errors.class && (
              <p className="mt-1 text-sm text-[#f38b83]">{errors.class.message}</p>
            )}
            {registrationData?.preferredClass && (
              <p className="mt-1 text-sm text-[#8a7f6e]">
                Originally selected: {registrationData.preferredClass}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderParentsDetails = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
        <Users className="w-5 h-5 mr-2 text-[#e2916d]" />
        Parents Details
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Father's Name *
          </label>
          <input
            type="text"
            {...register('fatherName', { required: 'Father\'s name is required' })}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter father's name"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-form-type="other"
          />
          {errors.fatherName && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.fatherName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Contact Number *
          </label>
          <input
            type="tel"
            {...register('parentContact', {
              required: 'Contact number is required',
              pattern: {
                value: /^[6-9]\d{9}$/,
                message: 'Please enter a valid 10-digit mobile number'
              }
            })}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="10-digit mobile number"
            readOnly={!!registrationData?.parentContact}
            onBlur={() => !registrationData?.parentContact && validateFieldAndShowToast('parentContact')}
          />
          {errors.parentContact && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.parentContact.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Email Address *
          </label>
          <input
            type="email"
            {...register('parentEmail', {
              required: 'Email address is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Please enter a valid email address'
              }
            })}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter email address"
            readOnly={!!registrationData?.parentEmail}
          />
          {errors.parentEmail && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.parentEmail.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Father Occupation
          </label>
          <input
            type="text"
            {...register('fatherOccupation')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter father's occupation"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Mother's Name *
          </label>
          <input
            type="text"
            value={motherNameValue ?? ''}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter mother's name"
            id="field-mother-name-parent-details"
            data-field="motherName"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-form-type="other"
            onChange={(e) => {
              const newValue = e.target.value;
              console.log('Mother Name field changed to:', newValue);

              // Get current values to verify no cross-contamination
              const beforeCaste = getValues('caste');
              const beforeReligion = getValues('religion');

              // Set ONLY the motherName field
              setValue('motherName', newValue, { shouldValidate: false });

              // Manual validation for Mother Name
              if (newValue.trim() === '') {
                setError('motherName', { type: 'required', message: 'Mother\'s name is required' });
              } else {
                clearErrors('motherName');
              }

              // Verify other fields were not affected
              const afterCaste = getValues('caste');
              const afterReligion = getValues('religion');

              if (beforeCaste !== afterCaste) {
                console.error('CRITICAL ERROR: Caste value changed when editing Mother Name!');
                console.error('Before:', beforeCaste, 'After:', afterCaste);
                // Restore the original caste value
                setValue('caste', beforeCaste);
              }

              if (beforeReligion !== afterReligion) {
                console.error('CRITICAL ERROR: Religion value changed when editing Mother Name!');
                console.error('Before:', beforeReligion, 'After:', afterReligion);
                // Restore the original religion value
                setValue('religion', beforeReligion);
              }

              console.log('After motherName change - Caste:', afterCaste, 'Religion:', afterReligion);
            }}
          />
          {errors.motherName && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.motherName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Mother Occupation
          </label>
          <input
            type="text"
            {...register('motherOccupation')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter mother's occupation"
          />
        </div>

      </div>
    </div>
  );

  const monthlyFeeStructure = feeStructures.find(
    fs => fs.className === watchedClass && fs.type === 'monthly' && fs.isActive
  );

  const renderFeeDetails = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
        <CreditCard className="w-5 h-5 mr-2 text-[#e2916d]" />
        Fee Details
      </h3>

      {watchedClass && (
        <div className="flex items-center space-x-2 text-sm">
          <span className="text-[#8a7f6e]">Monthly fee for {watchedClass}:</span>
          {monthlyFeeStructure ? (
            <span className="text-[#f3ede2] font-medium">
              ₹{monthlyFeeStructure.totalAmount.toLocaleString()} — will be applied automatically on admission approval.
            </span>
          ) : (
            <span className="text-[#8a7f6e]">No monthly fee structure configured yet.</span>
          )}
        </div>
      )}

      {selectedFeeStructure ? (
        <div className="bg-[#211c15] p-6 rounded-lg border">
          <h4 className="text-lg font-medium text-[#f3ede2] mb-4">
            Fee Structure for {selectedFeeStructure.className} - {selectedFeeStructure.academicYear}
          </h4>

          <div className="space-y-3">
            {selectedFeeStructure.feeCategories.map((category, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-[#3a3122] last:border-b-0">
                <span className="text-[#b9ad9b]">{category.category}</span>
                <span className="font-medium text-[#f3ede2]">₹{category.amount.toLocaleString()}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-3 border-t-2 border-[#e2916d]">
              <span className="text-lg font-semibold text-[#f3ede2]">Total Amount</span>
              <span className="text-lg font-bold text-[#e2916d]">₹{selectedFeeStructure.totalAmount.toLocaleString()}</span>
            </div>
          </div>

          <input type="hidden" {...register('selectedFeeStructure')} />
          <input type="hidden" {...register('feeAmount')} />
        </div>
      ) : watchedClass ? (
        <div className="bg-[#332711] border border-[#4a3a19] p-4 rounded-lg">
          <p className="text-[#f9daa3]">
            Fee structure for {watchedClass} is not available. Please contact the administration.
          </p>
        </div>
      ) : (
        <div className="bg-[#f3ede2] p-4 rounded-lg">
          <p className="text-[#b9ad9b]">
            Please select a class in the previous step to view the fee structure.
          </p>
        </div>
      )}
    </div>
  );

  const renderDocumentsUpload = () => {
    const documentTypes = [
      { key: 'photo', label: 'Student Photo', required: true },
      { key: 'aadharCard', label: 'Aadhar Card', required: true },
      { key: 'birthCertificate', label: 'Birth Certificate', required: true },
      { key: 'transferCertificate', label: 'Transfer Certificate', required: false },
      { key: 'marksheet', label: 'Previous Marksheet', required: false },
      { key: 'medicalCertificate', label: 'Medical Certificate', required: false },
      { key: 'casteCertificate', label: 'Caste Certificate', required: watchedIsRteQuota },
      { key: 'incomeCertificate', label: 'Income Certificate', required: watchedIsRteQuota }
    ];

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
          <FileText className="w-5 h-5 mr-2 text-[#e2916d]" />
          Documents Upload
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {documentTypes.map((docType) => (
            <div key={docType.key} className="border border-[#3a3122] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-[#b9ad9b]">
                  {docType.label} {docType.required && '*'}
                </label>
                {documents[docType.key] && (
                  <button
                    type="button"
                    onClick={() => removeDocument(docType.key)}
                    className="text-[#f38b83] hover:text-[#f38b83]"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {documents[docType.key] ? (
                <div className="space-y-2">
                  {documentPreviews[docType.key] && (
                    <img
                      src={documentPreviews[docType.key]}
                      alt={docType.label}
                      className="w-full h-32 object-cover rounded border"
                    />
                  )}
                  <p className="text-sm text-[#4ade80]">
                    ✓ {documents[docType.key]?.name}
                  </p>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-[#4a4030] border-dashed rounded-lg cursor-pointer bg-[#28221a] hover:bg-[#211c15]">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-2 text-[#8a7f6e]" />
                    <p className="text-sm text-[#8a7f6e]">Click to upload</p>
                    <p className="text-xs text-[#8a7f6e]">PNG, JPG or PDF (MAX. 5MB)</p>
                  </div>
                  <input
                    type="file"
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] hidden"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          // Use toast instead of alert for better UX
                          // toast.error('File size should be less than 5MB');
                          return;
                        }
                        handleDocumentUpload(docType.key, file);
                      }
                    }}
                  />
                </label>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBankDetails = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
        <Briefcase className="w-5 h-5 mr-2 text-[#e2916d]" />
        Bank Details
      </h3>

      <div className="bg-[#f3ede2] p-4 rounded-lg mb-4">
        <p className="text-[#b9ad9b] text-sm">
          Bank details are optional but recommended for fee refunds and other transactions.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Bank Account Number
          </label>
          <input
            type="text"
            {...register('bankAccountNumber')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter account number"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Bank Name
          </label>
          <input
            type="text"
            {...register('bankName')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter bank name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            IFSC Code
          </label>
          <input
            type="text"
            {...register('ifscCode', {
              pattern: {
                value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                message: 'Please enter a valid IFSC code'
              }
            })}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter IFSC code"
          />
          {errors.ifscCode && (
            <p className="mt-1 text-sm text-[#f38b83]">{errors.ifscCode.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
            Account Holder Name
          </label>
          <input
            type="text"
            {...register('accountHolderName')}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            placeholder="Enter account holder name"
          />
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStudentDetails();
      case 2:
        return renderParentsDetails();
      case 3:
        return renderDocumentsUpload();
      case 4:
        return renderBankDetails();
      case 5:
        return renderFeeDetails();
      default:
        return null;
    }
  };

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-[#f3ede2] mb-2">
            Student Admission Form
          </h2>
          {/* Save status indicator */}
          <div className="flex items-center space-x-2">
            {saving ? (
              <div className="flex items-center text-[#60a5fa]">
                <div className="w-4 h-4 border-2 border-[#60a5fa] border-t-transparent rounded-full animate-spin mr-2" />
                <span className="text-sm">Saving...</span>
              </div>
            ) : lastSaved ? (
              <div className="flex items-center text-[#4ade80]">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">
                  Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ) : (
              <div className="flex items-center text-[#8a7f6e]">
                <span className="text-sm">Save on navigation</span>
              </div>
            )}
          </div>
        </div>
        <p className="text-[#b9ad9b]">
          Complete all sections to submit the admission application. Your progress is saved when you navigate between steps.
        </p>
      </div>

      {renderStepIndicator()}

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {renderCurrentStep()}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-[#3a3122]">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center px-4 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </button>
            )}
          </div>

          <div className="flex space-x-3">
            {/* Manual save button */}
            <button
              type="button"
              onClick={() => saveDraftData(currentStep)}
              disabled={saving}
              className="flex items-center px-4 py-2 border border-[#2a3d4d] text-[#60a5fa] hover:bg-[#16212b] disabled:bg-[#211c15] disabled:text-[#8a7f6e] rounded-lg transition-colors text-sm"
            >
              {saving ? (
                <div className="w-3 h-3 border-2 border-[#60a5fa] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <FileText className="w-3 h-3 mr-2" />
              )}
              Save Draft
            </button>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors"
              >
                Cancel
              </button>
            )}

            {currentStep < STEPS.length ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={saving}
                className="flex items-center px-6 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="flex items-center px-6 py-2 bg-[#4ade80] hover:bg-[#4ade80] disabled:bg-[#8a7f6e] text-white rounded-lg transition-colors"
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                )}
                Submit Application
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default StudentAdmissionForm;